// Blender assets are loaded once at the boot gate. Scene instances own their
// material variants and GPU instance buffers; this catalog owns source meshes
// and shared compressed textures across every room transition.
import * as THREE from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { KTX2Loader } from "three/addons/loaders/KTX2Loader.js";
import { clone as cloneSkeleton } from "three/addons/utils/SkeletonUtils.js";

export const AUTHORED_KITS = [
  "vehicles",
  "nature",
  "fauna",
  "frontier",
  "city",
  "interiors",
  "space",
  "character",
] as const;
export type AuthoredKit = (typeof AUTHORED_KITS)[number];
export interface AuthoredAssetEntry {
  id: string;
  kit: AuthoredKit;
  url: string;
  bytes: number;
  triangles: number;
  primitives: number;
  [key: string]: unknown;
}
interface KitManifest {
  version: number;
  assets: Record<string, Omit<AuthoredAssetEntry, "id" | "kit">>;
  textures?: Array<{ url: string; bytes: number }>;
}
export interface AuthoredPlacement {
  x: number;
  y: number;
  z: number;
  yaw?: number;
  scale?: number | readonly [number, number, number];
  width?: number;
  height?: number;
  depth?: number;
}

const entries = new Map<string, AuthoredAssetEntry>();
const templates = new Map<string, THREE.Group>();
const ownedMaterials = new WeakSet<THREE.Material>();
const kitJobs = new Map<AuthoredKit, Promise<void>>();
const countedTextures = new Set<string>();
let bytes = 0;

export const authoredBytes = (): number => bytes;
export const authoredAssetEntries = (): AuthoredAssetEntry[] => [...entries.values()];
export const getAuthoredTemplate = (id: string): THREE.Group | undefined => templates.get(id);

/** One decode and Texture per shared content-hashed URL, across all GLB files. */
class SharedKTX2Loader extends KTX2Loader {
  private readonly files = new Map<string, Promise<THREE.CompressedTexture>>();

  override load(
    url: string,
    onLoad?: (texture: THREE.CompressedTexture) => void,
    onProgress?: (event: ProgressEvent) => void,
    onError?: (error: unknown) => void,
  ): void {
    let job = this.files.get(url);
    if (!job) {
      job = new Promise((resolve, reject) => super.load(url, resolve, onProgress, reject));
      this.files.set(url, job);
    }
    void job.then(onLoad).catch((error: unknown) => {
      this.files.delete(url);
      onError?.(error);
    });
  }
}

export async function preloadAuthoredAssets(
  renderer?: THREE.WebGLRenderer,
  kits: readonly AuthoredKit[] = AUTHORED_KITS,
): Promise<void> {
  if (kits.every((kit) => kitJobs.has(kit))) {
    await Promise.all(kits.map((kit) => kitJobs.get(kit)));
    return;
  }
  const probe = renderer ?? new THREE.WebGLRenderer();
  const textureLoader = new SharedKTX2Loader().detectSupport(probe).setWorkerLimit(3);
  const loader = new GLTFLoader().setKTX2Loader(textureLoader);
  const maxAniso = Math.min(8, probe.capabilities.getMaxAnisotropy());
  try {
    for (const kit of kits) {
      if (kitJobs.has(kit)) continue;
      const job = (async () => {
        const response = await fetch(`/assets/authored/${kit}/manifest.json`, {
          cache: "no-cache",
        });
        if (!response.ok) throw new Error(`Authored ${kit} manifest: HTTP ${response.status}`);
        const manifest = (await response.json()) as KitManifest;
        if (manifest.version !== 1 || !manifest.assets) throw new Error(`Invalid ${kit} manifest`);
        for (const texture of manifest.textures ?? []) {
          if (!countedTextures.has(texture.url)) {
            countedTextures.add(texture.url);
            bytes += texture.bytes;
          }
        }
        const records = Object.entries(manifest.assets);
        // Four GLBs per kit in flight bounds parsing/upload work while the
        // KTX2 pool limits the shared decoder CPU budget.
        let next = 0;
        await Promise.all(
          Array.from({ length: Math.min(4, records.length) }, async () => {
            for (;;) {
              const record = records[next++];
              if (!record) return;
              const [id, entry] = record;
              if (typeof entry.url !== "string" || !entry.url.startsWith("/assets/authored/")) {
                throw new Error(`Invalid authored asset URL: ${kit}/${id}`);
              }
              if (entries.has(id)) throw new Error(`Duplicate authored asset id: ${id}`);
              // An HTTP base preserves host-root KTX2 references in glTF;
              // a relative base makes Three prefix the kit directory twice.
              const gltf = await loader.loadAsync(new URL(entry.url, window.location.href).href);
              gltf.scene.name = id;
              gltf.scene.updateMatrixWorld(true);
              gltf.scene.traverse((object) => {
                if (!(object instanceof THREE.Mesh)) return;
                object.castShadow = true;
                object.receiveShadow = true;
                object.userData.authoredAsset = id;
                const materials = Array.isArray(object.material)
                  ? object.material
                  : [object.material];
                for (const material of materials) {
                  for (const value of Object.values(material)) {
                    if (value instanceof THREE.Texture) value.anisotropy = maxAniso;
                  }
                }
              });
              templates.set(id, gltf.scene);
              entries.set(id, { ...entry, id, kit } as AuthoredAssetEntry);
              bytes += Number(entry.bytes) || 0;
            }
          }),
        );
      })();
      kitJobs.set(kit, job);
    }
    // Finish active decodes before disposing their pool, including when one
    // manifest or model failed; no worker is torn down under another GLB.
    const results = await Promise.allSettled(kits.map((kit) => kitJobs.get(kit)));
    const failure = results.find((result) => result.status === "rejected");
    if (failure?.status === "rejected") throw failure.reason;
  } finally {
    textureLoader.dispose();
    if (!renderer) probe.dispose();
  }
}

function cloneMaterials(root: THREE.Object3D): void {
  const copies = new Map<THREE.Material, THREE.Material>();
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    const copy = (original: THREE.Material) => {
      let material = copies.get(original);
      if (!material) {
        material = original.clone();
        copies.set(original, material);
        ownedMaterials.add(material);
      }
      return material;
    };
    object.material = Array.isArray(object.material)
      ? object.material.map(copy)
      : copy(object.material);
    const beforeRender = object.onBeforeRender;
    object.onBeforeRender = function (renderer, scene, camera, geometry, material, group) {
      // Runtime spawns (traffic, creatures and player cairns) can arrive after
      // a room's initial probe binding. Give every owned PBR variant that same
      // probe on its first draw, including after moving between rooms.
      if (material instanceof THREE.MeshStandardMaterial && ownedMaterials.has(material)) {
        const intensity =
          typeof material.userData.authoredProbeGain === "number"
            ? material.userData.authoredProbeGain
            : scene.environmentIntensity < 0.2
              ? 0.4
              : scene.environmentIntensity;
        if (material.envMap !== scene.environment || material.envMapIntensity !== intensity) {
          // A probe-less interior must also clear a previous room's probe.
          material.envMap = scene.environment;
          material.envMapIntensity = intensity;
          material.needsUpdate = true;
        }
      }
      beforeRender.call(this, renderer, scene, camera, geometry, material, group);
    };
  });
}

export function cloneAuthoredAsset(id: string): THREE.Group {
  const source = templates.get(id);
  if (!source) throw new Error(`Authored asset ${id} was not loaded at the boot gate`);
  const result = cloneSkeleton(source) as THREE.Group;
  cloneMaterials(result);
  return result;
}

export function instanceAuthoredAsset(
  id: string,
  placements: readonly AuthoredPlacement[],
): THREE.Group {
  const source = templates.get(id);
  if (!source) throw new Error(`Authored asset ${id} was not loaded at the boot gate`);
  const group = new THREE.Group();
  if (placements.length === 0) return group;
  const bounds = new THREE.Box3().setFromObject(source);
  const size = bounds.getSize(new THREE.Vector3());
  const center = bounds.getCenter(new THREE.Vector3());
  const origin = new THREE.Matrix4().makeTranslation(-center.x, -bounds.min.y, -center.z);
  const transform = new THREE.Object3D();
  const matrix = new THREE.Matrix4();
  source.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    if (object instanceof THREE.SkinnedMesh) throw new Error(`Cannot instance skinned asset ${id}`);
    const mesh = new THREE.InstancedMesh(object.geometry, object.material, placements.length);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.userData.authoredAsset = id;
    placements.forEach((placement, index) => {
      const fitting =
        placement.width !== undefined ||
        placement.height !== undefined ||
        placement.depth !== undefined;
      transform.position.set(placement.x, placement.y, placement.z);
      transform.rotation.set(0, placement.yaw ?? 0, 0);
      const scale = placement.scale ?? 1;
      if (typeof scale === "number") transform.scale.setScalar(scale);
      else transform.scale.set(scale[0], scale[1], scale[2]);
      if (fitting) {
        transform.scale.multiply(
          new THREE.Vector3(
            placement.width === undefined ? 1 : placement.width / Math.max(size.x, 0.00001),
            placement.height === undefined ? 1 : placement.height / Math.max(size.y, 0.00001),
            placement.depth === undefined ? 1 : placement.depth / Math.max(size.z, 0.00001),
          ),
        );
      }
      transform.updateMatrix();
      matrix.copy(transform.matrix);
      if (fitting) matrix.multiply(origin);
      matrix.multiply(object.matrixWorld);
      mesh.setMatrixAt(index, matrix);
    });
    mesh.computeBoundingSphere();
    group.add(mesh);
  });
  cloneMaterials(group);
  return group;
}

export function bindAuthoredEnvironment(
  root: THREE.Object3D,
  environment: THREE.Texture,
  intensity = 1,
): void {
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh) || !object.userData.authoredAsset) return;
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    for (const material of materials) {
      if (!(material instanceof THREE.MeshStandardMaterial)) continue;
      // Instances made by this module always own their material variants.
      if (!ownedMaterials.has(material)) continue;
      material.userData.authoredProbeGain = intensity;
      material.envMap = environment;
      material.envMapIntensity = intensity;
      material.needsUpdate = true;
    }
  });
}

export function disposeAuthoredObject(root: THREE.Object3D): void {
  const disposed = new Set<THREE.Material>();
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    if (object instanceof THREE.InstancedMesh) object.dispose();
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    for (const material of materials) {
      if (ownedMaterials.has(material) && !disposed.has(material)) {
        material.dispose();
        disposed.add(material);
      }
    }
    if (object instanceof THREE.SkinnedMesh) object.skeleton.dispose();
  });
}
